import React, {useEffect, useRef, useState} from 'react';
import {Box, Container, IconButton, Typography} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface LecturerProps {
    name: string;
    position: string;
    image: string;
}

const LecturersSection: React.FC = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const lecturers: LecturerProps[] = [
        {
            name: "Сергій Квіт",
            position: "Президент НаУКМА",
            image: "kvit.png"
        },
        {
            name: "Олена Прімєрова",
            position: "Завідувачка кафедри фінансів НаУКМА",
            image: "primerova.png"
        },
        {
            name: "Марія Кукушкіна",
            position: "Деканка-організаторка медичної школи НаУКМА",
            image: "kukushkina.jpeg"
        },
        {
            name: "Максим Гончар",
            position: "Викладач демократичної школи «Майбутні», співавтор подкасту «Початок історії», guest speaker в kmbs, випускник НаУКМА",
            image: "gonchar.jpg"
        },
        {
            name: "Сергій Ноздрачов",
            position: "Викладач kmbs, сертифікований експерт Gallup з розвитку сильних сторін",
            image: "nozdrachov.webp"
        },
        {
            name: "Михайло Таран",
            position: "Радник президента НаУКМА з інновацій, керівник напряму рекрутингу абітурієнтів, випускник НаУКМА",
            image: "taran.JPEG"
        },
        {
            name: "Матвій Хренов",
            position: "Старший викладач НаУКМА, співзасновник Українського центру охорони здоров’я, генеральний директор медичного центру «Відновлення»",
            image: "hrenov.jpg"
        },
        {
            name: "Костянтин Якуненко",
            position: "Старший викладач кафедри фінансів, член правління CFA Society Ukraine",
            image: "jakunenko.JPG"
        },
        {
            name: "Первін Дадашова",
            position: "Директорка департаменту фінансової стабільності НБУ, випускниця НаУКМА",
            image: "dadashova.jpg"
        },
        {
            name: "Євгеній Сова",
            position: "Старший викладач кафедри фінансів НаУКМА, випускник НаУКМА",
            image: "sova.JPG"
        },
        {
            name: "Юлія Новицька",
            position: "Клінічна онкологиня мережі «Добробут»",
            image: "novitska.avif"
        },
    ];

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const itemWidth = container.clientWidth / 3;
            const scrollAmount = itemWidth * 3;
            const newScrollPosition = direction === 'left'
                ? container.scrollLeft - scrollAmount
                : container.scrollLeft + scrollAmount;

            container.scrollTo({
                left: newScrollPosition,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        handleScroll();
        window.addEventListener('resize', handleScroll);
        return () => window.removeEventListener('resize', handleScroll);
    }, []);

    return (
        <Container id={"lecturers"} maxWidth="lg" sx={{pt: {xs: 6, md: 8}, position: 'relative'}}>
            <Typography
                variant="h2"
                sx={{
                    fontSize: {xs: '2.5rem', md: '4rem'},
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: {xs: 4, md: 6},
                    fontFamily: 'Kyiv Type Serif'
                }}
            >
                Лектори та лекторки школи
            </Typography>

            <Box sx={{position: 'relative'}}>
                <IconButton
                    onClick={() => scroll('left')}
                    sx={{
                        position: 'absolute',
                        left: -20,
                        top: '40%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        '&:hover': {bgcolor: 'background.paper'},
                        display: canScrollLeft ? 'flex' : 'none',
                        boxShadow: 2
                    }}
                >
                    <ArrowBackIosNewIcon/>
                </IconButton>

                <Box
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {display: 'none'},
                        gap: 3,
                        py: 2,
                    }}
                >
                    {lecturers.map((lecturer, index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: '0 0 auto',
                                width: { xs: '260px', md: '300px' },
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{textAlign: 'center', width: '100%'}}>
                                <Box
                                    component="img"
                                    src={`lecturers/${lecturer.image}`}
                                    alt={`${lecturer.name} image`}
                                    sx={{
                                        width: '100%',
                                        height: { xs: '280px', md: '320px' },
                                        objectFit: 'cover',
                                        objectPosition: 'top center',
                                        borderRadius: '16px',
                                        mb: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 1,
                                        fontFamily: 'Kyiv Type Sans',
                                        fontSize: '1.2rem',
                                        lineHeight: 1.3
                                    }}
                                >
                                    {lecturer.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Kyiv Type Sans',
                                        fontSize: '0.95rem',
                                        color: '#555',
                                        lineHeight: 1.4
                                    }}
                                >
                                    {lecturer.position}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <IconButton
                    onClick={() => scroll('right')}
                    sx={{
                        position: 'absolute',
                        right: -20,
                        top: '40%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        '&:hover': {bgcolor: 'background.paper'},
                        display: canScrollRight ? 'flex' : 'none',
                        boxShadow: 2
                    }}
                >
                    <ArrowForwardIosIcon/>
                </IconButton>
            </Box>
        </Container>
    );
};

export default LecturersSection;